
// Deployment trigger: 2026-04-24
interface SendSmsParams {
  toPhone: string;
  message: string;
}

/**
 * Sanitizes and validates a phone number for US-only delivery.
 * Automatically adds +1 if missing.
 */
const sanitizeUSPhone = (phone: string): string => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // US numbers must be 10 digits (without prefix) or 11 (with '1' prefix)
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  throw new Error("Invalid US number. Please enter a 10-digit phone number (e.g., 2025550123). International numbers are not supported.");
};

/**
 * Calls our Cloudflare Backend (Functions) to send an SMS via SignalWire.
 */
export const sendSignalWireSms = async (params: SendSmsParams) => {
  try {
    const sanitizedPhone = sanitizeUSPhone(params.toPhone);
    
    const response = await fetch('/api/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        ...params,
        toPhone: sanitizedPhone
      })
    });

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await response.text();
      throw new Error(`The SMS server returned an unexpected response format. Please verify your SignalWire settings.`);
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `SMS Error: ${response.statusText}`);
    }

    return data;
  } catch (error: any) {
    console.error("SMS Service Error:", error);
    throw error;
  }
};
